import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js';
import 'leaflet.path.drag';
import 'leaflet-editable';

import MMapView from './MMap.view';

import { ILatLng } from '../../models/latlng';
import { MMapRoute, IMMapMarker, IMMapPolyline, MMapRef, MMapCustomControlButton } from './MMap.model';
import { formatMeasurementDistance } from './MMap.utils';
import classes from './MMap.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'models/app-store';
import { SettingsCategories } from 'models/settings';
import { FaLocationPin } from 'react-icons/fa6';
import { CgRuler } from 'react-icons/cg';
import * as uiActions from 'store/actions/ui';
import { useTranslation } from 'react-i18next';
import useMeasureSystemType from 'utils/hooks/measure-system-type';
import { GeoSystemTypes } from 'utils/enums/geo-system-types';
import { MapTileStyles } from 'utils/enums/map-tile-styles';

type Props<Clusters extends boolean> = {
  polygons?: ILatLng[];
  fullScreen?: boolean;
  editable?: boolean;
  scrollWheelZoom?: boolean;
  className?: string;
  zoomControl?: boolean;
  markers?: IMMapMarker<Clusters>[];
  clusterMarers?: IMMapMarker<Clusters>[];
  unfocusedMarkers?: IMMapMarker<Clusters>[];
  polylines?: IMMapPolyline[];
  routes?: MMapRoute[];
  map?: L.Map;
  style?: React.CSSProperties;
  selectedPolygons?: L.LatLng[][];
  fitBounds?: boolean;
  clusters?: Clusters;
  tileLayer?: MapTileStyles;
  zoom?: number;
  padding?: [number, number];
  paddingTopLeft?: [number, number];
  paddingBottomRight?: [number, number];
  mapCenter?: [number, number];
  zoomControlClassName?: string;
  customControlButtons?: MMapCustomControlButton[];
  measurementTool?: boolean;
  mapOverlayElement?: JSX.Element;
  radius?: number;
  onMarkerClusterClick?: (marker: IMMapMarker<true>) => void;
  onPolylineClick?: (id: string) => void;
  onChangePolygone?: (locations: L.LatLng[]) => void;
  polylineToEdit?: L.LatLng[];
  onChangePolyline?: (coordinates?: L.LatLng[]) => void;
};

const getTileUrl = (style: MapTileStyles, darkMode: boolean): string => {
  switch (style) {
    case MapTileStyles.Streets:
      return import.meta.env.VITE_TILES_SERVER_STREETS;
    case MapTileStyles.Satellite:
      return import.meta.env.VITE_TILES_SERVER_SATELLITE;
    default:
      return darkMode ? import.meta.env.VITE_TILES_SERVER_DARK : import.meta.env.VITE_TILES_SERVER;
  }
};

const MMap = <Clusters extends boolean = false>(props: React.PropsWithChildren<Props<Clusters>>, ref: React.ForwardedRef<MMapRef>) => {
  const markers_clusters = props.clusterMarers as IMMapMarker<true>[];
  const markersInClusterRef = useRef<Map<string, L.Marker>>(new Map<string, L.Marker>());
  const featureGroupRef = useRef<L.FeatureGroup | null>(null);
  const darkMode = useSelector((state: AppState) => state.ui.darkMode);
  const mapCenter = useSelector((state: AppState) => state.settings.settings?.[SettingsCategories.Geolocation]['mapCenter']);
  const mapZoom = useSelector((state: AppState) => state.settings.settings?.[SettingsCategories.Geolocation]['mapZoom']) || 2;
  const mapStyle = useSelector((state: AppState) => state.settings.settings?.[SettingsCategories.Geolocation]['mapStyle']) || MapTileStyles.LightDark;
  const activeMapStyle = props.tileLayer ?? mapStyle;

  const [mapCencterState, setMapCenterState] = useState<[number, number]>(props.mapCenter || [0, 0]);
  const [mapZoomState, setMapZoomState] = useState<number>(props.zoom || mapZoom);
  const [mapState, setMapState] = useState<L.Map | null>(null);
  const [isFullscreenState, setIsFullscreenState] = useState<boolean>(false);
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapHeight = props.style?.height;
  const mapWidth = props.style?.width;

  const { t } = useTranslation();
  const { geoSystemType, converter } = useMeasureSystemType();

  useEffect(() => {
    if (!mapState || !props.mapCenter || !props.radius) {
      return;
    }

    const circle = L.circle(props.mapCenter, {
      radius: converter(props.radius, geoSystemType === GeoSystemTypes.Metric ? 'meters' : 'feet', 'meters'),
      color: 'grey',
      weight: 0.9,
      fillColor: '#D2E0F8',
      fillOpacity: 0.3,
    }).addTo(mapState!);

    mapState.fitBounds(circle.getBounds());

    return () => {
      mapState.removeLayer(circle);
    };
  }, [mapState, props.mapCenter, props.radius, geoSystemType]);

  useEffect(() => {
    if (!mapState) {
      return;
    }

    const scaleControl = L.control.scale({
      position: 'bottomright',
      metric: geoSystemType === GeoSystemTypes.Metric,
      imperial: geoSystemType === GeoSystemTypes.Imperial,
    });

    scaleControl.addTo(mapState);

    return () => {
      scaleControl.remove();
    };
  }, [mapState, geoSystemType]);

  const [measurementModeState, setMeasurementModeState] = useState<boolean>(false);
  const [measurementPointsState, setMeasurementPointsState] = useState<L.LatLng[]>([]);

  const toggleMeasurementMode = () => {
    setMeasurementModeState((prev: boolean) => !prev);
    setMeasurementPointsState(() => []);
  };

  useEffect(() => {
    if (!mapState) {
      return;
    }

    mapState.getContainer().style.cursor = measurementModeState ? 'crosshair' : '';
  }, [mapState, measurementModeState]);

  useEffect(() => {
    if (!mapState || !measurementModeState) {
      return;
    }

    const handleMapClick = (event: L.LeafletMouseEvent) => {
      setMeasurementPointsState((prev: L.LatLng[]) => (prev.length >= 2 ? [event.latlng] : [...prev, event.latlng]));
    };

    mapState.on('click', handleMapClick);

    return () => {
      mapState.off('click', handleMapClick);
    };
  }, [mapState, measurementModeState]);

  useEffect(() => {
    if (!mapState || measurementPointsState.length === 0) {
      return;
    }

    const layers: L.Layer[] = measurementPointsState.map((point: L.LatLng) =>
      L.circleMarker(point, { radius: 5, className: 'mmap-measurement-point' }).addTo(mapState),
    );

    if (measurementPointsState.length === 2) {
      const [pointA, pointB] = measurementPointsState;
      const distanceInMeters = pointA.distanceTo(pointB);
      const distanceLabel = formatMeasurementDistance(distanceInMeters, geoSystemType, converter);

      const line = L.polyline([pointA, pointB], { className: 'mmap-measurement-line' })
        .addTo(mapState)
        .bindTooltip(distanceLabel, { permanent: true, direction: 'center', className: 'mmap-measurement-tooltip' })
        .openTooltip();

      layers.push(line);
    }

    return () => {
      layers.forEach((layer: L.Layer) => mapState.removeLayer(layer));
    };
  }, [mapState, measurementPointsState, geoSystemType, converter]);

  const measurementButton = useMemo<MMapCustomControlButton | null>(() => {
    if (!props.measurementTool) {
      return null;
    }

    return {
      id: 'measurementTool',
      name: t('common.map.measureDistance', 'Measure Distance'),
      icon: CgRuler,
      onClick: toggleMeasurementMode,
      ...(measurementModeState && { className: classes['measurementButtonActive'] }),
    };
  }, [props.measurementTool, measurementModeState, t]);

  const customControlButtons = useMemo<MMapCustomControlButton[]>(() => {
    return [...(props.customControlButtons || []), ...(measurementButton ? [measurementButton] : [])];
  }, [props.customControlButtons, measurementButton]);

  useImperativeHandle(ref, () => {
    return {
      map: mapState,
      clearLayers: () => {
        featureGroupRef?.current?.clearLayers();
        props.onChangePolygone?.([] as L.LatLng[]);
      },
    };
  }, [mapState, props.mapCenter, props.radius]);

  useEffect(() => {
    if (mapZoom) {
      setMapZoomState(() => mapZoom);
    }
  }, [mapZoom, setMapZoomState]);

  useEffect(() => {
    if (!mapState) {
      return;
    }

    mapState.on('fullscreenchange', function () {
      if (!mapState.isFullscreen()) {
        setIsFullscreenState(() => false);
      }
    });
  }, [mapState]);

  const toggleFullscreen = () => {
    mapState?.toggleFullscreen();
    setIsFullscreenState(() => !mapState?.isFullscreen());
  };

  useEffect(() => {
    if (mapCenter) {
      setMapCenterState(() => [mapCenter.lat, mapCenter.lng] as [number, number]);
    }
  }, [mapCenter, setMapCenterState]);

  useEffect(() => {
    if (typeof props.zoom === 'number') {
      setMapZoomState(() => props.zoom!);
    }
  }, [props.zoom, setMapZoomState]);

  useEffect(() => {
    if (props.mapCenter) {
      setMapCenterState(() => [props.mapCenter![0], props.mapCenter![1]]);
    }
  }, [props.mapCenter, setMapCenterState]);

  useEffect(() => {
    if (mapState) {
      mapState.setView([mapCencterState[0], mapCencterState[1]], mapZoomState);
    }
  }, [mapState, mapCencterState, mapZoomState]);

  useEffect(() => {
    if (!mapState || !props.fitBounds || !props.markers || props.markers.length <= 0) {
      return;
    }

    mapState.fitBounds(
      props.markers.map((marker: IMMapMarker<Clusters>) => [marker.coord.lat, marker.coord.lng]),
      isFullscreenState
        ? { padding: props.padding }
        : {
            padding: props.padding,
            paddingBottomRight: props.paddingBottomRight,
            paddingTopLeft: props.paddingTopLeft,
            maxZoom: props.zoom || mapZoom,
          },
    );
  }, [mapState, props.fitBounds, props.padding, props.paddingBottomRight, props.paddingTopLeft, isFullscreenState, props.zoom]);

  useEffect(() => {
    if (!mapState) {
      return;
    }

    const invalidateSize = () => mapState.invalidateSize();
    const initialTimeout = setTimeout(invalidateSize, 200);

    let observer: ResizeObserver | null = null;

    if (containerRef.current) {
      observer = new ResizeObserver(() => invalidateSize());
      observer.observe(containerRef.current);
    }

    return () => {
      clearTimeout(initialTimeout);
      observer?.disconnect();
    };
  }, [mapState, mapHeight, mapWidth, isFullscreenState]);

  useEffect(() => {
    if (!mapState || !props.selectedPolygons) {
      return;
    }

    const otherAreas = props.selectedPolygons.map((selectedArea: L.LatLng[]) => {
      const newPol = L.polygon(selectedArea, { color: 'blue', weight: 1.5 });

      newPol.addTo(mapState);

      return newPol;
    });

    return () => {
      otherAreas.forEach((area: L.Polygon) => area.remove());
    };
  }, [props.selectedPolygons, mapState]);

  // *****Clusters******
  const markersCluster: L.MarkerClusterGroup | null = useMemo(() => {
    if (!props.clusters) {
      return null;
    }

    const markers = L.markerClusterGroup({
      iconCreateFunction(cluster: L.MarkerCluster) {
        return L.divIcon({
          html: ReactDOMServer.renderToStaticMarkup(
            <div className={classes['cluster']}>
              <span className={classes['cluster__iconWrapper']}>
                <FaLocationPin size={40}></FaLocationPin>
              </span>
              <div className={classes['cluster__counter']}>{cluster.getChildCount()}</div>,
            </div>,
          ),
        });
      },
      showCoverageOnHover: false,
    });

    return markers;
  }, [props.clusters]);

  useEffect(() => {
    if (!mapState) {
      return;
    }

    const layer = new L.TileLayer(getTileUrl(activeMapStyle, darkMode));

    layer.addTo(mapState);

    return () => {
      mapState.removeLayer(layer);
    };
  }, [mapState, activeMapStyle, darkMode]);

  useEffect(() => {
    if (!markersCluster || !mapState) {
      return;
    }

    mapState.addLayer(markersCluster);

    return () => {
      mapState.removeLayer(markersCluster);
    };
  }, [mapState, markersCluster]);

  useEffect(() => {
    if (!markersCluster) {
      return;
    }

    const markerIds = markers_clusters.map((marker: IMMapMarker<true>) => marker.id);

    // Removes markers that were removed from props.markers
    markersInClusterRef.current.forEach((value: L.Marker, id: string) => {
      if (!markerIds.includes(id)) {
        markersCluster.removeLayer(value);
        markersInClusterRef.current.delete(id);
      }
    });

    // Add new markers that were added to props.markers and update those who exist
    markers_clusters.forEach((marker: IMMapMarker<true>) => {
      // Exist
      if (markersInClusterRef.current.has(marker.id)) {
        markersInClusterRef.current.get(marker.id)?.setLatLng({ lat: marker.coord.lat, lng: marker.coord.lng });

        if (marker.icon) {
          markersInClusterRef.current.get(marker.id)?.setIcon(marker.icon);
        }

        return;
      }

      // New
      const markerLayer = L.marker({ lat: marker.coord.lat, lng: marker.coord.lng }, { icon: marker.icon });

      markerLayer.on('click', () => {
        props.onMarkerClusterClick?.(marker);
      });

      markersInClusterRef.current.set(marker.id, markerLayer);
      markersCluster.addLayer(markerLayer);
    });
  }, [markersCluster, markersInClusterRef, markers_clusters]);

  const markers = useMemo(() => {
    return [...(props.markers || []), ...(props.unfocusedMarkers || [])];
  }, [props.markers, props.unfocusedMarkers]);

  const createdPolygonHandler = (event: L.DrawEvents.Created) => {
    const { layerType, layer } = event;

    if (layerType === 'polygon') {
      const coordinates = (layer as L.Polygon).getLatLngs();

      props.onChangePolygone?.(coordinates[0] as L.LatLng[]);
    }
  };

  const editedPolygonHandler = (event: L.DrawEvents.Edited) => {
    event.layers.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        const newCoordinates = layer.getLatLngs();

        props.onChangePolygone?.(newCoordinates[0] as L.LatLng[]);
      }
    });
  };

  useEffect(() => {
    if (!mapState || !props.polygons?.length) {
      return;
    }

    // Ensure only one polygon can be created
    featureGroupRef?.current?.clearLayers();

    const polygon = L.polygon(props.polygons);

    featureGroupRef.current?.addLayer(polygon);

    setTimeout(() => {
      mapState.fitBounds(polygon.getBounds(), {
        padding: props.padding,
        paddingBottomRight: props.paddingBottomRight,
        paddingTopLeft: props.paddingTopLeft,
      });
    }, 500);
  }, [mapState, props.polygons, props.editable, props.padding, props.paddingBottomRight, props.paddingTopLeft]);

  const deletePolygonHandler = (closeModal: () => void) => {
    closeModal();

    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }

    props.onChangePolygone?.([] as L.LatLng[]);
  };

  const handleDeleteClick = () => {
    dispatch(
      uiActions.showConfirmModal({
        title: t('common.map.polygon.deleteModal.header'),
        body: t('common.map.polygon.deleteModal.body'),
        onConfirm: deletePolygonHandler,
      }),
    );
  };

  return (
    <MMapView
      containerRef={containerRef}
      zoom={props.zoom ?? mapZoomState}
      markers={markers}
      center={[mapCencterState[0], mapCencterState[1]]}
      onCreate={(map: L.Map) => setMapState(() => map)}
      editable={props.editable}
      className={props.className}
      scrollWheelZoom={props.scrollWheelZoom}
      onFullScreenClick={toggleFullscreen}
      isMapFullScreen={isFullscreenState}
      hideFullScreenControl={props.fullScreen === false}
      hideZoomControls={props.zoomControl === false}
      onZoomInClick={() => mapState?.setZoom(mapState.getZoom() + 1)}
      onZoomOutClick={() => mapState?.setZoom(mapState.getZoom() - 1)}
      style={props.style}
      polylines={props.polylines}
      routes={props.routes}
      clusters={props.clusters}
      zoomControlClassName={props.zoomControlClassName}
      onPolylineClick={props.onPolylineClick}
      customControlButtons={customControlButtons}
      mapOverlayElement={props.mapOverlayElement}
      onCreatedPolygon={createdPolygonHandler}
      onEditedPolygon={editedPolygonHandler}
      onDeletedPolygon={handleDeleteClick}
      featureGroupRef={featureGroupRef}
      polygons={props.polygons}
      polylineToEdit={props.polylineToEdit}
      onChangePolyline={props.onChangePolyline}
    ></MMapView>
  );
};

MMap.displayName = 'MMap';

export default React.forwardRef(MMap);
